import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Users, Star, Zap, Heart, ChevronDown, X, MapPin, BookOpen, Briefcase, Globe } from 'lucide-react';
import { VIBE_TYPE_AVATARS } from '../data/vibeAvatars';

const API = process.env.REACT_APP_BACKEND_URL;

const VIBE_TYPES = [
  {
    id: 'social_anchor', emoji: '🏠', label: 'The Social Anchor', color: '#F59E0B',
    desc: 'Makes a house a home. Always cooking, chatting, hosting.',
    gender: 'Female',
    origin: 'Nigerian-Australian · Melbourne, VIC',
    lifestyle: [
      'Up by 7am, makes breakfast for the whole house',
      'Hosts weekly family-style dinners — everyone\'s invited',
      'Kitchen is always stocked, fridge is always open',
      'Weekends mean farmer\'s markets and Sunday roasts',
    ],
    work: 'Nursing student, placement three days a week. Collaborative, team-first, great under pressure.',
    study: 'Bachelor of Nursing, University of Melbourne',
    culture: 'West African heritage with deep community values. Family gatherings, rich food traditions, and a home that always feels welcoming to strangers.',
    traits: ['Warm', 'Nurturing', 'Community-driven', 'Great cook'],
  },
  {
    id: 'quiet_professional', emoji: '📚', label: 'The Quiet Professional', color: '#6366F1',
    desc: 'Respectful, tidy, independent. Gives and needs space.',
    gender: 'Male',
    origin: 'German-Australian · Sydney, NSW',
    lifestyle: [
      'Gym at 6:30am, coffee black, desk by 8am',
      'Work from home 4 days, in-office for team meetings',
      'Shared spaces always spotless — dishwasher emptied without asking',
      'Weekends are either hiking or completely offline',
    ],
    work: 'Fintech software engineer. Deep-focus worker, minimal interruptions. After-hours are strictly off.',
    study: 'Bachelor of Computer Science, UNSW Sydney',
    culture: 'Central European work ethic mixed with Australian laid-back weekends. Values precision, privacy, and mutual respect above all.',
    traits: ['Reliable', 'Tidy', 'Independent', 'Focused'],
  },
  {
    id: 'night_owl', emoji: '🌙', label: 'The Night Owl', color: '#7C3AED',
    desc: 'Comes alive after 10pm. Needs housemates who respect that.',
    gender: 'Female',
    origin: 'Jamaican-Australian · Brisbane, QLD',
    lifestyle: [
      'Creative mode kicks in after 9pm — music, art, writing',
      'Mornings are slow: no loud alarms, no 7am conversations',
      'Common areas kept tidy, just not at conventional hours',
      'Weekly chill sessions — vinyl records, incense, good conversation',
    ],
    work: 'Freelance graphic designer and part-time music curator. Thrives on async work, delivers before deadlines.',
    study: 'Bachelor of Design (Visual Communication), QUT',
    culture: 'Caribbean heritage blended with Australian creative scene. Music, colour, storytelling, and a deeply expressive sense of self.',
    traits: ['Creative', 'Nocturnal', 'Expressive', 'Low-drama'],
  },
  {
    id: 'early_riser', emoji: '☀️', label: 'The Early Riser', color: '#F97316',
    desc: 'Mornings are sacred. Running, breakfasts, asleep by 10.',
    gender: 'Male',
    origin: 'Colombian-Australian · Perth, WA',
    lifestyle: [
      '5:30am runs along the river before anyone else wakes up',
      'Meal preps every Sunday — meals for the whole week',
      'Gym 4 times a week, football on Saturdays',
      'Lights out by 10pm, screens off by 9:30',
    ],
    work: 'Sports physiotherapy student and part-time personal trainer. Structured schedule, early finishes.',
    study: 'Bachelor of Health Science (Physiotherapy), Curtin University',
    culture: 'South American family culture meets Australian sporting lifestyle. Passionate about community, shared meals, and keeping active.',
    traits: ['Disciplined', 'Active', 'Social', 'Structured'],
  },
  {
    id: 'free_spirit', emoji: '🌿', label: 'The Free Spirit', color: '#10B981',
    desc: 'Flexible, easygoing, adapts to whoever they live with.',
    gender: 'Female',
    origin: 'Spanish-Australian · Byron Bay / Melbourne, VIC',
    lifestyle: [
      'Morning yoga and journalling before coffee',
      'Plant-based cooking, herbs on the windowsill',
      'Flexible routine — some days 7am, some days 10am',
      'Weekend drives, coastal walks, farmers markets',
    ],
    work: 'UX researcher and part-time ceramics teacher. Flexible hours, deep empathy, human-centred everything.',
    study: 'Master of Sustainability and Design, RMIT University',
    culture: 'Mediterranean warmth mixed with Australian coastal mindfulness culture. Values presence, creativity, and meaningful connection over schedules.',
    traits: ['Adaptable', 'Mindful', 'Creative', 'Calm'],
  },
  {
    id: 'homebody', emoji: '🛋️', label: 'The Homebody', color: '#8B5CF6',
    desc: 'Home is a sanctuary. Quiet, cosy, deeply personal.',
    gender: 'Male',
    origin: 'Indian-Australian · Sydney, NSW',
    lifestyle: [
      'Home is a sanctuary — shoes off at the door, always',
      'Cooks from scratch most nights, loves sharing the leftovers',
      'Board game nights, movie marathons, comfort playlists',
      'Rarely out past 9pm by choice, not circumstance',
    ],
    work: 'Full-stack developer, fully remote. Deep work in a quiet home is non-negotiable.',
    study: 'Bachelor of Software Engineering, University of Sydney',
    culture: 'South Asian values of hospitality and home comfort mixed with millennial Aussie tech culture. Home isn\'t just where you sleep — it\'s where you live.',
    traits: ['Cosy', 'Reliable', 'Hospitable', 'Homebody'],
  },
];

const SAMPLE_PROFILES = [
  { name: 'Priya S.', age: 22, uni: 'University of Sydney', type: 'The Social Anchor', vibeKey: 'social_anchor', emoji: '🏠', color: '#F59E0B', score: 91, prompt: '"I love hosting — my housemates always know there\'s a home-cooked meal waiting."' },
  { name: 'Marcus T.', age: 25, uni: 'RMIT University', type: 'The Early Riser', vibeKey: 'early_riser', emoji: '☀️', color: '#F97316', score: 87, prompt: '"Living with me means random good coffee on Sunday mornings."' },
  { name: 'Aisha M.', age: 21, uni: 'UQ Brisbane', type: 'The Free Spirit', vibeKey: 'free_spirit', emoji: '🌿', color: '#10B981', score: 84, prompt: '"Peaceful home, good books, zero drama — that\'s my vibe."' },
];

const STEPS = [
  { icon: '🧠', title: 'Take the Vibe Quiz', desc: '10 questions that reveal your living personality. Not your Netflix taste — your actual home habits.' },
  { icon: '💫', title: 'Swipe on matches', desc: 'See your daily 20 curated matches with compatibility scores. Left, right, or Super Vibe.' },
  { icon: '🏡', title: 'Form your group', desc: 'Matched? Start a group together and find your third housemate — then search for your property.' },
];

export default function VibemateLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeUsers, setActiveUsers] = useState(0);
  const [cardIdx, setCardIdx] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [selectedVibe, setSelectedVibe] = useState(null);

  useEffect(() => {
    // Fetch real active user count
    fetch(`${API}/api/vibemate/stats`, {
      headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
    }).then(r => r.ok ? r.json() : { active_users: 0 })
      .then(d => setActiveUsers(d.active_users || 0))
      .catch(() => setActiveUsers(0));

    // Auto-cycle sample cards
    const t = setInterval(() => setCardIdx(i => (i + 1) % SAMPLE_PROFILES.length), 3500);
    return () => clearInterval(t);
  }, []);

  const handleCTA = () => {
    if (user) navigate('/vibemate/app');
    else navigate('/login', { state: { from: '/vibemate/app' } });
  };

  const card = SAMPLE_PROFILES[cardIdx];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden">

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight text-white">Rental</span>
            <img
              src="/rentalx-x-new.svg"
              alt="X"
              style={{
                height: '1.5rem',
                width: 'auto',
                display: 'inline-block',
                verticalAlign: 'middle',
                marginLeft: '0.05em',
                marginBottom: '0.02em',
              }}
            />
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <button onClick={() => navigate('/vibemate/app')}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                Open VibeMate
              </button>
            ) : (
              <>
                <Link to="/login" className="text-white/60 hover:text-white text-sm transition-colors">Sign In</Link>
                <button onClick={handleCTA}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            {activeUsers > 0 && (
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs text-white/80 mb-6 border border-white/10">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                {activeUsers}+ people finding housemates right now
              </div>
            )}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight mb-4 tracking-tight">
              Find housemates who actually match your
              <span style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}> vibe</span>
            </h1>
            <p className="text-xl text-white/60 mb-8 leading-relaxed">
              Not just your budget.
            </p>
            <p className="text-white/50 text-base mb-10 max-w-md leading-relaxed">
              VibeMate matches students and professionals on living compatibility — sleep schedules, noise levels, cleanliness, lifestyle. The stuff that actually matters.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={handleCTA}
                className="flex items-center gap-2 px-7 py-4 rounded-2xl text-base font-bold text-white hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                Find My Vibe Match <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#vibe-types"
                className="flex items-center gap-2 px-7 py-4 rounded-2xl text-base font-semibold text-white/70 border border-white/15 hover:border-white/30 hover:text-white transition-all">
                What&apos;s your Vibe Type?
              </a>
            </div>
          </div>

          {/* Animated card preview */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-72">
              <div className="relative bg-gradient-to-b from-white/10 to-white/5 rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
                style={{ height: 460 }}>
                {/* Avatar image */}
                <div className="h-56 w-full overflow-hidden relative"
                  style={{ background: `linear-gradient(135deg,${card.color}33,${card.color}11)` }}>
                  {VIBE_TYPE_AVATARS[card.vibeKey]
                    ? <img src={VIBE_TYPE_AVATARS[card.vibeKey]} alt={card.type}
                        className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full flex items-center justify-center text-7xl">{card.emoji}</div>
                  }
                </div>
                {/* Match score */}
                <div className="absolute top-4 right-4 flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full border-4 border-[#2cbdc4] flex items-center justify-center bg-black/60">
                    <span className="text-xs font-black text-white">{card.score}%</span>
                  </div>
                  <span className="text-[9px] text-white/60 mt-1">match</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-bold text-white">{card.name}, {card.age}</h3>
                  </div>
                  <p className="text-xs text-white/50 mb-3">{card.uni}</p>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-4"
                    style={{ backgroundColor: `${card.color}25`, color: card.color, border: `1px solid ${card.color}40` }}>
                    {card.emoji} {card.type}
                  </div>
                  <p className="text-xs text-white/60 italic leading-relaxed">{card.prompt}</p>
                </div>
              </div>
              {/* Action buttons */}
              <div className="flex items-center justify-center gap-4 mt-5">
                <button className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center text-xl border border-white/10 hover:bg-red-500/20 transition-colors">❌</button>
                <button className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-lg border border-white/10 hover:bg-yellow-500/20 transition-colors">⚡</button>
                <button className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center text-xl border border-white/10 hover:bg-green-500/20 transition-colors">💚</button>
              </div>
              {/* Dots */}
              <div className="flex justify-center gap-1.5 mt-4">
                {SAMPLE_PROFILES.map((_, i) => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === cardIdx ? 'bg-[#2cbdc4] w-4' : 'bg-white/20'}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-xs text-[#2cbdc4] font-bold uppercase tracking-widest mb-4">How it works</p>
          <h2 className="text-4xl font-black text-center mb-14">Three steps to your perfect housemate</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((step, i) => (
              <div key={i} className="relative bg-white/5 rounded-2xl p-7 border border-white/10 hover:border-white/20 transition-colors">
                <div className="text-4xl mb-4">{step.icon}</div>
                <div className="absolute top-6 right-6 text-6xl font-black text-white/5 select-none">{i + 1}</div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vibe Types */}
      <section id="vibe-types" className="py-20 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-xs text-[#2cbdc4] font-bold uppercase tracking-widest mb-4">Vibe Types</p>
          <h2 className="text-4xl font-black text-center mb-4">Which one are you?</h2>
          <p className="text-center text-white/50 mb-12 max-w-lg mx-auto">
            Six living personalities. Your Vibe Type is calculated from your quiz answers and shapes who you match with.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {VIBE_TYPES.map(vt => (
              <div key={vt.id}
                onClick={() => setSelectedVibe(vt)}
                className="relative bg-white/5 rounded-2xl border border-white/10 hover:scale-105 hover:border-white/25 transition-all cursor-pointer group overflow-hidden"
                style={{ borderColor: `${vt.color}30` }}>
                {/* Avatar image top half */}
                <div className="h-40 w-full overflow-hidden rounded-t-2xl"
                  style={{ background: `linear-gradient(160deg,${vt.color}25,${vt.color}08)` }}>
                  {VIBE_TYPE_AVATARS[vt.id]
                    ? <img src={VIBE_TYPE_AVATARS[vt.id]} alt={vt.label}
                        className="w-full h-full object-cover object-top" loading="lazy" />
                    : <div className="w-full h-full flex items-center justify-center text-5xl">{vt.emoji}</div>
                  }
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-white mb-1 text-sm">{vt.label}</h3>
                  <p className="text-white/45 text-xs leading-relaxed">{vt.desc}</p>
                  <p className="text-xs mt-2 font-semibold" style={{ color: vt.color }}>Tap to learn more →</p>
                </div>
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: `linear-gradient(135deg,${vt.color}08,transparent)` }} />
              </div>
            ))}
          </div>

          {/* Vibe Type Detail Modal */}
          {selectedVibe && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: 'rgba(4,30,43,0.85)', backdropFilter: 'blur(12px)' }}
              onClick={() => setSelectedVibe(null)}>
              <div
                className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
                style={{ background: 'linear-gradient(160deg,#0d2535,#083a54)', border: `1.5px solid ${selectedVibe.color}40` }}
                onClick={e => e.stopPropagation()}>
                {/* Close */}
                <button onClick={() => setSelectedVibe(null)}
                  className="absolute top-4 right-4 z-10 w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <X className="w-4 h-4 text-white" />
                </button>

                {/* Avatar hero */}
                <div className="h-56 w-full relative overflow-hidden"
                  style={{ background: `linear-gradient(160deg,${selectedVibe.color}30,${selectedVibe.color}10)` }}>
                  {VIBE_TYPE_AVATARS[selectedVibe.id]
                    ? <img src={VIBE_TYPE_AVATARS[selectedVibe.id]} alt={selectedVibe.label}
                        className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full flex items-center justify-center text-7xl">{selectedVibe.emoji}</div>
                  }
                  {/* Gradient overlay */}
                  <div className="absolute inset-0"
                    style={{ background: 'linear-gradient(to top, #083a54 0%, transparent 60%)' }} />
                  {/* Label over image */}
                  <div className="absolute bottom-4 left-5 right-16">
                    <div className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full mb-1.5"
                      style={{ background: `${selectedVibe.color}30`, color: selectedVibe.color, border: `1px solid ${selectedVibe.color}50` }}>
                      {selectedVibe.emoji} {selectedVibe.gender}
                    </div>
                    <h2 className="text-2xl font-black text-white">{selectedVibe.label}</h2>
                  </div>
                </div>

                {/* Scrollable content */}
                <div className="p-6 space-y-5 max-h-[55vh] overflow-y-auto"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: `${selectedVibe.color}40 transparent` }}>

                  {/* Origin */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${selectedVibe.color}20` }}>
                      <Globe className="w-4 h-4" style={{ color: selectedVibe.color }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-0.5">Origin &amp; Cultural Background</p>
                      <p className="text-sm text-white font-semibold">{selectedVibe.origin}</p>
                      <p className="text-xs text-white/55 leading-relaxed mt-1">{selectedVibe.culture}</p>
                    </div>
                  </div>

                  {/* Study */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${selectedVibe.color}20` }}>
                      <BookOpen className="w-4 h-4" style={{ color: selectedVibe.color }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-0.5">Education</p>
                      <p className="text-sm text-white/80 leading-relaxed">{selectedVibe.study}</p>
                    </div>
                  </div>

                  {/* Work */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${selectedVibe.color}20` }}>
                      <Briefcase className="w-4 h-4" style={{ color: selectedVibe.color }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-0.5">Work Pattern</p>
                      <p className="text-sm text-white/80 leading-relaxed">{selectedVibe.work}</p>
                    </div>
                  </div>

                  {/* Lifestyle */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${selectedVibe.color}20` }}>
                      <MapPin className="w-4 h-4" style={{ color: selectedVibe.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Lifestyle at Home</p>
                      <ul className="space-y-1.5">
                        {selectedVibe.lifestyle.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-white/70">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                              style={{ background: selectedVibe.color }} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Trait pills */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {selectedVibe.traits.map(t => (
                      <span key={t} className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ background: `${selectedVibe.color}18`, color: selectedVibe.color, border: `1px solid ${selectedVibe.color}35` }}>
                        {t}
                      </span>
                    ))}
                  </div>

                  <button onClick={handleCTA}
                    className="w-full py-3.5 rounded-2xl font-bold text-white text-sm hover:opacity-90 transition-opacity mt-2"
                    style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                    Take the quiz — find your Vibe Type
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="text-center mt-10">
            <button onClick={handleCTA}
              className="px-8 py-4 rounded-2xl font-bold text-white hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
              Take the Quiz — Find Your Type
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <Zap className="w-6 h-6" />, color: '#2cbdc4', title: 'Vibe Type Matching', desc: 'A real compatibility engine — not just budget and suburb. Sleep schedules, noise levels, guests, kitchen habits.' },
            { icon: <Users className="w-6 h-6" />, color: '#8B5CF6', title: 'Group Formation', desc: "Matched with someone great? Start a group together and find your third housemate before searching for a property." },
            { icon: <Star className="w-6 h-6" />, color: '#F59E0B', title: 'Daily Curation', desc: '20 quality matches per day. Scarcity makes each profile worth evaluating. 3 Super Vibes to use on your strongest matches.' },
          ].map((f, i) => (
            <div key={i} className="bg-white/5 rounded-2xl p-7 border border-white/10">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${f.color}20`, color: f.color }}>
                {f.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-4">Built for Australian students and professionals</h2>
          <p className="text-white/50 mb-12">
            Join people already using VibeMate across Sydney, Melbourne, Brisbane, and Wollongong
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['🎓 Students', '💼 Professionals', '🏠 Cities', '💫 Vibe Types'].map((item, i) => (
              <div key={i} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-2xl font-black text-white mb-1">{['2x faster', '5+ cities', '4 states', '6 types'][i]}</p>
                <p className="text-white/40 text-xs">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 border-t border-white/5 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-5xl font-black mb-4">Ready to find your vibe?</h2>
          <p className="text-white/50 mb-10 text-lg">Take the 10-question quiz. Get your Vibe Type. Start matching.</p>
          <button onClick={handleCTA}
            className="px-10 py-5 rounded-2xl text-lg font-bold text-white hover:opacity-90 transition-opacity shadow-2xl"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            Find My Vibe Match →
          </button>
          <p className="text-white/30 text-xs mt-4">Free to join. No credit card required.</p>
        </div>
      </section>

      {/* Footer strip */}
      <div className="border-t border-white/5 py-6 px-6 text-center">
        <p className="text-white/30 text-xs">VibeMate is part of the RentalX platform · <Link to="/" className="hover:text-white/60 transition-colors">rentalx.io</Link></p>
      </div>
    </div>
  );
}
